import { ChevronDown } from "lucide-react";
import { Tabs as TabsPrimitive } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";

/**
 * The strip above a list: view tabs on the left, drawn as underlined text, with the
 * search and ordering controls on the right. It runs edge to edge across the page and
 * draws one rule underneath; the active tab's underline sits on that rule.
 */
export function ViewToolbar({
  tabs,
  children,
  className,
}: {
  tabs: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "-mx-gutter flex min-h-10 flex-wrap items-end justify-between gap-x-4 border-b px-gutter",
        className,
      )}
    >
      {tabs}
      {children && <div className="flex flex-wrap items-center gap-2 py-1.5">{children}</div>}
    </div>
  );
}

/** A second strip under the toolbar for filter chips and selection controls. */
export function ViewChipsBar({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("-mx-gutter flex flex-wrap items-center gap-2 border-b px-gutter py-2", className)}
      {...props}
    />
  );
}

/*
 * These sit on the Radix primitives rather than the shadcn TabsList and TabsTrigger, whose
 * pill styling and underline offset are tuned for a padded list and fight any override.
 */
export function ViewTabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="view-tabs-list"
      className={cn("flex items-end gap-5 text-muted-foreground", className)}
      {...props}
    />
  );
}

const VIEW_TAB_CLASS = cn(
  "relative inline-flex h-10 items-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-(--motion-dur) outline-none",
  "hover:text-foreground focus-visible:text-foreground",
  "focus-visible:after:opacity-60 disabled:pointer-events-none disabled:opacity-50",
  // The underline overlaps the strip's rule by a pixel so the two read as one line.
  "after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-foreground after:opacity-0 after:transition-opacity after:duration-(--motion-dur)",
);

export function ViewTab({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="view-tab"
      className={cn(
        VIEW_TAB_CLASS,
        "data-[state=active]:text-foreground data-[state=active]:after:opacity-100",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The last slot of a tab strip: a menu of the views people open less often. It reads as
 * the active tab, labelled with that view, while one of its views is showing. It sits
 * beside the tab list rather than inside it, since a menu button is not a tab.
 */
export function ViewTabMenu({
  label,
  active,
  children,
}: {
  label: ReactNode;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-slot="view-tab"
        data-active={active || undefined}
        className={cn(
          VIEW_TAB_CLASS,
          "text-muted-foreground data-[state=open]:text-foreground data-active:text-foreground data-active:after:opacity-100",
        )}
      >
        {label}
        <ChevronDown className="size-3.5 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-36">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
